import { NavLink, Outlet } from 'react-router';
import { PLATFORM_CAPABILITY_LABELS, type PlatformCapability } from '@gatecheckpro/contracts';
import { Notice, Status } from '@gatecheckpro/design-system';
import { useSession, useViewer } from '../lib/session.tsx';

/**
 * The operations console shell.
 *
 * Navigation is built from the operator's *granted* capabilities, one entry per
 * capability. There is no "administration" section that quietly aggregates
 * several: an onboarding reviewer sees onboarding and nothing else, and the
 * absence is visible rather than hidden behind a menu that would refuse them
 * (SDAD Section 9.5).
 */
interface ConsoleRoute {
  readonly to: string;
  readonly label: string;
  readonly capability: PlatformCapability;
}

const ROUTES: readonly ConsoleRoute[] = [
  { to: '/onboarding', label: 'Organizer onboarding', capability: 'OPS_ORGANIZER_ONBOARDING' },
  { to: '/payment-exceptions', label: 'Payment exceptions', capability: 'OPS_SUPPORT' },
  {
    to: '/finance-reconciliation',
    label: 'Reconciliation',
    capability: 'OPS_FINANCE_RECONCILIATION',
  },
  { to: '/audit', label: 'Audit', capability: 'OPS_SECURITY' },
  { to: '/capability-grants', label: 'Capability grants', capability: 'OPS_PLATFORM_ADMIN' },
];

export function ConsoleLayout() {
  const viewer = useViewer();
  const { signOut } = useSession();
  const held = viewer.platformCapabilities;
  const available = ROUTES.filter((route) => held.includes(route.capability));

  return (
    <div className="gc-app">
      <header className="gc-header">
        <div className="gc-container gc-header__inner">
          <NavLink to="/" className="gc-wordmark">
            GateCheck<span className="gc-wordmark__accent">Pro</span>
            <span className="gc-wordmark__suffix">Operations</span>
          </NavLink>

          <nav className="gc-nav" aria-label="Operations">
            {available.map((route) => (
              <NavLink key={route.to} to={route.to} className="gc-nav__link">
                {route.label}
              </NavLink>
            ))}
          </nav>

          <div className="gc-row" style={{ marginInlineStart: 'auto' }}>
            <Status
              tone={viewer.mfa.satisfiedForSession ? 'positive' : 'critical'}
              label={viewer.mfa.satisfiedForSession ? 'Second factor verified' : 'No second factor'}
              title="Every operations capability requires a session that proved a second factor."
            />
            <span className="gc-muted">{viewer.displayName}</span>
            <button className="gc-button gc-button--quiet" onClick={() => void signOut()}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose">
          {!viewer.mfa.satisfiedForSession && held.length > 0 && (
            <Notice tone="critical" title="This session cannot use any operations capability">
              You hold {held.map((capability) => PLATFORM_CAPABILITY_LABELS[capability]).join(', ')}
              , but this session did not present a second factor. Sign out and sign in again with
              your authenticator code.
            </Notice>
          )}
          <Outlet />
        </div>
      </main>

      <footer className="gc-footer">
        <div className="gc-container">
          CABS internal · every read and every decision here is recorded in the audit trail
        </div>
      </footer>
    </div>
  );
}
